import OrganizationUser from "byzantine/src/OrganizationUser";
import Role, { MODEL_STR } from "byzantine/src/Role";
import { ContextForm, useFormData } from "cerulean";
import { formatNumber } from "@narmi/design_system";
import PropTypes from "prop-types";
import React, { useState } from "react";
import utils from "../../utils";
import BaseBanner from "../BaseBanner";
import AccessLevelSection from "./AccessLevelSection";
import OrgUserActions from "./OrgUserActions";
import OrganizationAccessLevelExtraPermissions from "./OrganizationAccessLevelExtraPermissions";
import {
  bundleExtraPermissions,
  validateExtraPermissions,
} from "./OrganizationEditUser";
import UserInfoHeader from "./UserInfoHeader";
import UserInfoInputs from "./UserInfoInputs";

export default function OrganizationNewUser({
  accounts = [],
  externalAccounts = [],
  ...props
}) {
  const isUsingConsumerBillpay = !!JSON.parse(props.isUsingConsumerBillpayJson);
  const roles = JSON.parse(props.roles).map((role) => new Role(role));
  const [permissionsError, setPermissionsError] = useState(null);
  const defaultDailyACHLimit = props.features.limits?.ach_push?.[1]
    ? formatNumber(props.features.limits?.ach_push?.[1])
    : "";
  const defaultDailyWireLimit = props.features.limits?.wire?.[1]
    ? formatNumber(props.features.limits?.wire?.[1])
    : "";
  const defaultDailyACHPaymentLimit = props.features.limits
    ?.unverified_ach_push?.[1]
    ? formatNumber(props.features.limits?.unverified_ach_push?.[1])
    : "";
  const defaultDailyCollectACHPaymentLimit = props.features.limits
    ?.unverified_ach_pull?.[1]
    ? formatNumber(props.features.limits?.unverified_ach_pull?.[1])
    : "";
  const defaultDailyUnverifiedFedNowSendLimit = props.features.limits
    ?.fednow_unverified?.[1]
    ? formatNumber(props.features.limits?.fednow_unverified?.[1])
    : "";
  const defaultDailyACHPassthroughSendLimit = props.features.limits
    ?.ach_passthrough_send?.[1]
    ? formatNumber(props.features.limits?.ach_passthrough_send?.[1])
    : "";
  const defaultDailyACHPassthroughCollectLimit = props.features.limits
    ?.ach_passthrough_collect?.[1]
    ? formatNumber(props.features.limits?.ach_passthrough_collect?.[1])
    : "";
  // TODO will need to add international wires limit when that feature is available

  const initialData = {
    accounts,
    externalAccounts,
    extraPermissions: {},
    ach_limits: defaultDailyACHLimit.toString(),
    wire_limits: defaultDailyWireLimit.toString(),
    ach_payment_limits: defaultDailyACHPaymentLimit.toString(),
    collect_ach_payment_limits: defaultDailyCollectACHPaymentLimit.toString(),
    fednow_unverified_send_limits: defaultDailyUnverifiedFedNowSendLimit.toString(),
    ach_passthrough_send_limits: defaultDailyACHPassthroughSendLimit.toString(),
    ach_passthrough_collect_limits: defaultDailyACHPassthroughCollectLimit.toString(),
  };
  OrganizationAccessLevelExtraPermissions.affectedPermissions.forEach(
    (model) => {
      initialData.extraPermissions[model] = false;
    }
  );

  const { onChange, formData } = useFormData(initialData);

  const onSubmit = (callback) => {
    const error = validateExtraPermissions(formData);

    if (error) {
      setPermissionsError(error);
      callback();
      return;
    }
    formData.extraPermissions = bundleExtraPermissions(formData);
    let ach_limits = 0;
    let wire_limits = 0;
    let ach_payment_limits = 0;
    let collect_ach_payment_limits = 0;
    let fednow_unverified_send_limits = 0;
    let international_wire_limits = 0;
    let ach_passthrough_send_limits = 0;
    let ach_passthrough_collect_limits = 0;

    if (formData.extraPermissions[MODEL_STR.ACH_TRANSFER]) {
      ach_limits = utils.parseValueAsFloat(formData.ach_limits);
    }
    if (formData.extraPermissions[MODEL_STR.DOMESTIC_WIRE_TRANSACTION] || formData.extraPermissions[MODEL_STR.DOMESTIC_WIRE_VIA_TEMPLATES] || formData.extraPermissions[MODEL_STR.DOMESTIC_WIRE_IMPORT]) {
      wire_limits = utils.parseValueAsFloat(formData.wire_limits);
    }
    if (
      formData.extraPermissions[MODEL_STR.ACH_PAYMENT] ||
      formData.extraPermissions[MODEL_STR.ACH_PAYMENT_VIA_TEMPLATE] ||
      formData.extraPermissions[MODEL_STR.ACH_PAYMENT_VIA_IMPORT]
    ) {
      ach_payment_limits = utils.parseValueAsFloat(formData.ach_payment_limits);
    }
    if (
      formData.extraPermissions[MODEL_STR.COLLECT_ACH_PAYMENT] ||
      formData.extraPermissions[MODEL_STR.ACH_PAYMENT_COLLECT_VIA_TEMPLATE] ||
      formData.extraPermissions[MODEL_STR.ACH_PAYMENT_COLLECT_VIA_IMPORT]
    ) {
      collect_ach_payment_limits = utils.parseValueAsFloat(
        formData.collect_ach_payment_limits
      );
    }
    if (formData.extraPermissions[MODEL_STR.UNVERIFIED_FEDNOW_SEND]) {
      fednow_unverified_send_limits = utils.parseValueAsFloat(
        formData.fednow_unverified_send_limits
      );
    }
    if (formData.extraPermissions[MODEL_STR.INTERNATIONAL_WIRE] || formData.extraPermissions[MODEL_STR.INTERNATIONAL_WIRE_VIA_TEMPLATES]) {
      international_wire_limits = utils.parseValueAsFloat(
        formData.international_wire
      );
    }
    if (formData.extraPermissions[MODEL_STR.ACH_PASSTHROUGH_SEND]) {
      ach_passthrough_send_limits = utils.parseValueAsFloat(
        formData.ach_passthrough_send_limits
      );
    }
    if (formData.extraPermissions[MODEL_STR.ACH_PASSTHROUGH_COLLECT]) {
      ach_passthrough_collect_limits = utils.parseValueAsFloat(
        formData.ach_passthrough_collect_limits
      );
    }

    const newUser = new OrganizationUser({
      user: {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
      },
      status: OrganizationUser.STATUS.PENDING,
      role: Role.generateFromAccessLevelDeprecated(
        formData.accessLevel,
        formData.accounts,
        formData.externalAccounts,
        formData.extraPermissions,
        roles,
        {
          ach_limits,
          wire_limits,
          ach_payment_limits,
          collect_ach_payment_limits,
          international_wire_limits,
          fednow_unverified_send_limits,
          ach_passthrough_send_limits,
          ach_passthrough_collect_limits,
        }
      ),
      organization_uuid: props.orgId,
    });

    newUser
      .sendInviteDeprecated()
      .then((response) => {
        let infoToast = "";
        if (
          response.organization_user?.status ===
            OrganizationUser.STATUS.PENDING &&
          response?.organization_user?.demo_invite_url
        ) {
          infoToast = `&info=Demo+only:+click+[here](${response?.organization_user?.demo_invite_url})+to+access+the+invite+link.`;
        }
        window.location = `/manage_users?success=Invite+sent.${infoToast}`;
      })
      .catch(callback);
  };

  return (
    <div style={{ minHeight: "800px" }}>
      <BaseBanner
        bannerName="Add a new user"
        goBack={() => window.location.assign("/manage_users")}
      />
      <ContextForm data={formData} onChange={onChange}>
        <div className="section-box">
          <UserInfoHeader />
          <UserInfoInputs />
        </div>
        <AccessLevelSection
          formData={formData}
          features={props.features}
          roles={roles}
          organizationUsers={props.organizationUsers}
          errorMessage={permissionsError}
          onChange={onChange}
          isUsingConsumerBillpay={isUsingConsumerBillpay}
        />
        <OrgUserActions onSubmit={onSubmit} dangerouslyDisableShowLoading />
      </ContextForm>
    </div>
  );
}

OrganizationNewUser.propTypes = {
  accounts: PropTypes.array,
  externalAccounts: PropTypes.array,
  orgId: PropTypes.string.isRequired,
  roles: PropTypes.string.isRequired,
  features: PropTypes.object,
  organizationUsers: PropTypes.array.isRequired,
  isUsingConsumerBillpayJson: PropTypes.string,
};

